// The client-facing upload portal: a Google-Drive / file-explorer experience over
// an arbitrary-depth folder tree. Header is logo-only; the collection title lives
// in the breadcrumb. You navigate folders (breadcrumb + back), and upload into
// "slots". A Drive-style animated upload tray tracks progress. Polls + refreshes
// on focus so a second viewer reflects uploads. No login — the URL token is scope.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Upload, FileText, Film, CheckCircle, AlertCircle, X, RefreshCw, Folder, ChevronRight, ChevronLeft, Download, MessageSquare } from 'react-feather'
import type { CollectionView, TreeNode, FolderNode, SlotNode, UploadedFile } from '../types'
import { resolveCollection, uploadFile, deleteFile, saveAnswer, ApiError } from '../lib/api'
import { pathToHash, parseHash, clampPath, samePath } from '../lib/folderNav'
import { useFileUrl } from './FileUrlContext'
import { Logo, Spinner, formatBytes } from './ui'
import { QuestionsView, isAnswered } from './Questions'
import { type Job, TYPE_LABEL, typeLabel, findSlotNode, planUploads } from '../lib/uploadPlan'

const QV = '__questions__'   // reserved sentinel: path [QV] = the Questions panel

export { TYPE_LABEL, typeLabel }   // re-exported for callers that import them from Portal
// The slot type is a HINT (badge + note guide the client), not a hard filter:
// any file is accepted, so the picker is left unrestricted — important on mobile,
// where an `accept` would hide the Files app and block non-photo uploads.

function friendlyError(err: ApiError, name: string): string {
  switch (err.code) {
    case 'file_too_large': return `${name} is too large.`
    case 'type_not_allowed': case 'unrecognized_type': case 'ext_mismatch': return `${name} isn't an allowed file type.`
    case 'collection_full': case 'storage_full': case 'insufficient_storage': return 'Upload limit reached for this link.'
    case 'too_many_files': case 'slot_full': return 'This folder is full.'
    case 'infected': return `${name} failed a security scan and was rejected.`
    case 'rate_limited': return 'Too many uploads — wait a moment and retry.'
    case 'network': case 'timeout': case 'stalled': case 'client_aborted': case 'stream_error': case 'scan_unavailable':
      return `Connection dropped while uploading ${name}. Tap to retry.`
    default: return `Couldn't upload ${name}.`
  }
}
// A transient failure (network blip / stall / gateway / scan hiccup) is auto-retried a few
// times before it's ever surfaced as a manual "try again". A cap-style failure (folder/
// storage full, too large, wrong type, infected) is terminal — retrying can't fix it.
function isTransient(err: ApiError): boolean {
  return err.status === 0 || err.status === 502 || err.status === 503 || err.status === 504 ||
    ['network', 'timeout', 'stalled', 'client_aborted', 'stream_error', 'scan_unavailable'].includes(err.code || '')
}

export function nodesAt(tree: TreeNode[], path: string[]): TreeNode[] {
  let nodes = tree
  for (const seg of path) {
    const f = nodes.find((n): n is FolderNode => n.kind === 'folder' && n.name === seg)
    if (!f) return []
    nodes = f.children
  }
  return nodes
}
// rollup for a folder row: total slots + total uploaded files beneath it
export function rollup(node: TreeNode): { slots: number; files: number } {
  if (node.kind === 'slot') return { slots: 1, files: node.uploaded.length }
  return node.children.reduce((a, c) => { const r = rollup(c); return { slots: a.slots + r.slots, files: a.files + r.files } }, { slots: 0, files: 0 })
}
// immutably drop an uploaded file (by id) from the tree — for optimistic removal
export function pruneFile(nodes: TreeNode[], fileId: string): TreeNode[] {
  return nodes.map((n) => n.kind === 'folder'
    ? { ...n, children: pruneFile(n.children, fileId) }
    : { ...n, uploaded: n.uploaded.filter((f) => f.id !== fileId) })
}
// split a filename into base + extension so the base can ellipsis-truncate while
// the extension always stays visible ("ABCDEFGH….png").
function splitName(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf('.')
  return dot > 0 && dot < name.length - 1 ? { base: name.slice(0, dot), ext: name.slice(dot) } : { base: name, ext: '' }
}
const mimeKind = (m: string) => m.startsWith('image/') ? 'image' : m.startsWith('video/') ? 'video' : m === 'application/pdf' ? 'pdf' : 'file'
// view-aware clamp: preserve the Questions sentinel ONLY when the collection
// actually has questions (else a stale/hand-crafted #/__questions__ link would
// flash a literal "__questions__" crumb); otherwise clamp to a real folder path.
const clampView = (tree: TreeNode[], segs: string[], hasQuestions: boolean): string[] =>
  segs[0] === QV ? (hasQuestions ? [QV] : []) : clampPath(tree, segs)

export function Portal({ initial }: { initial: CollectionView }) {
  const [data, setData] = useState(initial)
  // Current folder path is restored FROM the URL hash on mount, so a refresh /
  // return-to-tab / shared deep link keeps the client where they were.
  const [path, setPath] = useState<string[]>(() => clampView(initial.tree, parseHash(window.location.hash), !!(initial.questions && initial.questions.length)))
  const [jobs, setJobs] = useState<Job[]>([])
  const [lightbox, setLightbox] = useState<UploadedFile | null>(null)
  const jobId = useRef(0)
  // Refs mirror the latest state for the (mount-once) popstate handler.
  const pathRef = useRef(path)
  const treeRef = useRef(data.tree)
  const questionsRef = useRef(data.questions)
  const jobsRef = useRef(jobs)   // latest jobs for the (out-of-updater) capacity gate + retry
  useEffect(() => { pathRef.current = path }, [path])
  useEffect(() => { treeRef.current = data.tree }, [data.tree])
  useEffect(() => { questionsRef.current = data.questions }, [data.questions])
  useEffect(() => { jobsRef.current = jobs }, [jobs])

  // Navigate to a folder path: update the view AND push a clean history entry
  // (root → no hash) so the browser Back button steps out of folders too.
  const navigate = useCallback((next: string[]) => {
    setPath(next)
    const want = pathToHash(next)
    if (want !== window.location.hash) {
      window.history.pushState(null, '', window.location.pathname + window.location.search + want)
    }
  }, [])

  const refresh = useCallback(async () => { try { setData(await resolveCollection()) } catch { /* keep last good */ } }, [])
  useEffect(() => {
    const iv = setInterval(refresh, 12000)
    const onFocus = () => refresh()
    // Browser Back/Forward (history entries we pushed) → sync the view to the hash.
    const onPop = () => {
      const next = clampView(treeRef.current, parseHash(window.location.hash), !!(questionsRef.current && questionsRef.current.length))
      if (!samePath(next, pathRef.current)) setPath(next)
    }
    window.addEventListener('focus', onFocus)
    window.addEventListener('popstate', onPop)
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); window.removeEventListener('popstate', onPop) }
  }, [refresh])

  // If a poll/refresh changes the tree and the current deep folder no longer
  // exists, clamp the view to the nearest valid folder (don't strand the client).
  // The Questions sentinel stays only while the collection still has questions.
  useEffect(() => {
    setPath((cur) => {
      if (cur[0] === QV) return (data.questions && data.questions.length) ? cur : []
      const c = clampPath(data.tree, cur); return samePath(c, cur) ? cur : c
    })
  }, [data.tree, data.questions])

  const level = useMemo(() => nodesAt(data.tree, path), [data.tree, path])
  const folders = level.filter((n): n is FolderNode => n.kind === 'folder')
  const slots = level.filter((n): n is SlotNode => n.kind === 'slot')
  const atRoot = path.length === 0
  const questions = data.questions || []
  const inQuestions = path[0] === QV && questions.length > 0
  const answeredCount = questions.filter(isAnswered).length

  // Launch ONE file's XHR and wire its progress/done/error into its own job. Shared by
  // the first attempt AND Retry. Fired OUTSIDE any state updater — firing it inside the
  // setJobs updater (the old code) let React's replayable updater + a stale slot snapshot
  // decide the destination, which surfaced as cross-slot contamination + miscounts.
  const enqueueRef = useRef<(id: number, path: string, file: File) => void>(() => {})
  const launch = useCallback((id: number, path: string, file: File) => {
    // Smooth bytes/sec (EWMA over short samples) → the tray's "1.2 MB/s · ~2 min left".
    let lastT = Date.now(), lastLoaded = 0, bps = 0
    return uploadFile(path, file, (frac, loaded, total) => {
      const now = Date.now(), dt = (now - lastT) / 1000
      if (dt >= 0.4) { const inst = Math.max(0, loaded - lastLoaded) / dt; bps = bps ? bps * 0.6 + inst * 0.4 : inst; lastT = now; lastLoaded = loaded }
      setJobs((js) => js.map((j) => (j.id === id ? { ...j, progress: frac, loaded, total, bps, retrying: false } : j)))
    })
      .then(() => { setJobs((js) => js.map((j) => (j.id === id ? { ...j, progress: 1, status: 'done', retrying: false } : j))); refresh() })
      .catch((e) => {
        const err = e as ApiError
        const attempt = jobsRef.current.find((j) => j.id === id)?.attempt || 0
        if (isTransient(err) && attempt < 3) {
          // Self-heal a transient mobile-network blip: keep the row in-progress
          // (Reconnecting…) and re-enqueue after a backoff — no dead-end "try again".
          setJobs((js) => js.map((j) => (j.id === id ? { ...j, status: 'uploading', retrying: true, progress: 0, attempt: attempt + 1, error: undefined } : j)))
          setTimeout(() => enqueueRef.current(id, path, file), [1500, 4000, 9000][attempt] || 9000)
        } else {
          setJobs((js) => js.map((j) => (j.id === id ? { ...j, status: 'error', retrying: false, error: friendlyError(err, file.name) } : j)))
        }
      })
  }, [refresh])

  // Bounded upload concurrency: a client can pick a WHOLE phone gallery (hundreds of files)
  // into one open slot. Firing every XHR at once bursts past the per-minute rate limit (429)
  // and hammers the server/clamd. So we QUEUE picked files and run at most POOL in parallel;
  // the rest drain automatically as uploads finish. A LARGE file (video, >25MB) runs ALONE —
  // several big videos sharing a phone uplink just makes each one slower + likelier to stall.
  const POOL = 5, BIG = 25 * 1024 * 1024
  const uploadQueue = useRef<{ id: number; path: string; file: File }[]>([])
  const activeUploads = useRef(0)
  const bigActive = useRef(false)
  const pump = useCallback(() => {
    while (uploadQueue.current.length) {
      if (bigActive.current) break                            // a big file is running alone — wait it out
      const next = uploadQueue.current[0]
      const big = next.file.size > BIG
      if (big && activeUploads.current > 0) break             // a big file starts only when nothing else is active
      if (activeUploads.current >= POOL) break
      uploadQueue.current.shift()
      activeUploads.current++
      if (big) bigActive.current = true
      launch(next.id, next.path, next.file).finally(() => { activeUploads.current--; if (big) bigActive.current = false; pump() })
    }
  }, [launch])
  const enqueue = useCallback((id: number, path: string, file: File) => { uploadQueue.current.push({ id, path, file }); pump() }, [pump])
  useEffect(() => { enqueueRef.current = enqueue }, [enqueue])

  // Upload picked files into a slot. We SNAPSHOT the destination path now, gate capacity
  // against the LIVE tree (findSlotNode → freshest uploaded count, not a render-stale
  // snapshot), build pure job rows via planUploads, commit them in a pure updater, THEN
  // launch the network calls. Destination can no longer drift between slots.
  const upload = useCallback((slot: SlotNode, files: FileList | File[]) => {
    const path = slot.path
    const arr = Array.from(files)
    if (!arr.length) return
    const live = findSlotNode(treeRef.current, path)                       // null for the answer-image pseudo-slot
    const uploadedCount = live ? live.uploaded.length : slot.uploaded.length
    const slotCap = live ? live.count : slot.count
    const plan = planUploads({ prevJobs: jobsRef.current, uploadedCount, slotCap, slotPath: path, files: arr, alloc: () => ++jobId.current })
    setJobs((prev) => [...prev, ...plan.jobs])                             // PURE: only computes state
    for (const { id, file } of plan.launch) enqueue(id, path, file)       // queued + pooled, bound to the snapshotted path
  }, [enqueue])

  // Retry a failed upload with the File we kept on the job. Re-gate capacity first (a
  // fixed-count slot may have filled since), so retry can't be a guaranteed re-fail.
  const retry = useCallback((id: number) => {
    const job = jobsRef.current.find((j) => j.id === id)
    if (!job || job.status === 'uploading' || !job.file) return
    const path = job.slotPath
    const live = findSlotNode(treeRef.current, path)
    const cap = live ? live.count : Infinity                              // pseudo-slot (answer image) → server gates
    const uploadedCount = live ? live.uploaded.length : 0
    const inflight = jobsRef.current.filter((j) => j.status === 'uploading' && j.slotPath === path).length
    if (cap - uploadedCount - inflight <= 0) {
      setJobs((js) => js.map((j) => (j.id === id ? { ...j, status: 'error', error: 'This folder is full.' } : j)))
      return
    }
    setJobs((js) => js.map((j) => (j.id === id ? { ...j, status: 'uploading', progress: 0, error: undefined } : j)))
    enqueue(id, path, job.file)
  }, [enqueue])

  const retryAll = useCallback(() => { for (const j of jobsRef.current) if (j.status === 'error' && j.file) retry(j.id) }, [retry])

  // Remove one uploaded file (client fixes a mistake). Optimistic: drop it from
  // the tree AND any question answer-images, then reconcile with the server.
  const removeFile = useCallback((file: UploadedFile) => {
    setData((d) => ({
      ...d,
      tree: pruneFile(d.tree, file.id),
      questions: d.questions ? d.questions.map((q) => q.answer ? { ...q, answer: { ...q.answer, files: q.answer.files.filter((f) => f.id !== file.id) } } : q) : d.questions,
    }))
    deleteFile(file.id).then(refresh).catch(refresh)
  }, [refresh])

  // Save one question's answer (autosave) + reconcile the progress counters.
  const onSaveAnswer = useCallback((questionId: string, value: { selected?: string[]; other_text?: string; text?: string }) => {
    saveAnswer(questionId, value).then(refresh).catch(refresh)
  }, [refresh])

  // Attach answer image(s) to a question — reuse the slot upload machinery (tray +
  // progress) by addressing the reserved path Questions/<questionId>.
  const onPickAnswerImages = useCallback((questionId: string, files: FileList | File[]) => {
    const q = (data.questions || []).find((x) => x.id === questionId)
    const pseudo = { kind: 'slot', name: 'image', note: '', type: 'photo', count: 10, path: `Questions/${questionId}`, uploaded: q?.answer?.files || [] } as unknown as SlotNode
    upload(pseudo, files)
  }, [data.questions, upload])

  const dismissJob = (id: number) => setJobs((js) => js.filter((j) => j.id !== id))
  // auto-clear only DONE jobs a few seconds after they settle. ERROR jobs persist
  // (with their dismiss X) — the tray is the only place an upload failure surfaces,
  // so a failed file must never silently vanish.
  useEffect(() => {
    if (!jobs.some((j) => j.status === 'done')) return
    const t = setTimeout(() => setJobs((js) => js.filter((j) => j.status !== 'done')), 4000)
    return () => clearTimeout(t)
  }, [jobs])

  return (
    <div className="min-h-dvh" style={{ background: 'var(--color-canvas)' }}>
      {/* Header — logo + one action only */}
      <header className="sticky top-0 z-20 safe-top" style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-line)' }}>
        <div className="mx-auto flex items-center px-4 sm:px-6" style={{ maxWidth: 920, height: 60 }}>
          <Logo />
          <div className="flex-1" />
          <button className="btn--icon" onClick={refresh} aria-label="Refresh"><RefreshCw size={18} /></button>
        </div>
      </header>

      <main className="mx-auto px-4 sm:px-6 py-4 rf-fade-in" style={{ maxWidth: 920 }}>
        {/* Breadcrumb + back */}
        <div className="flex items-center gap-1 flex-wrap py-1" style={{ minHeight: 36 }}>
          {!atRoot && (
            <button className="btn--icon" style={{ marginRight: 2 }} aria-label="Back" onClick={() => navigate(path.slice(0, -1))}><ChevronLeft size={18} /></button>
          )}
          <Crumb label={data.title} onClick={() => navigate([])} active={atRoot} root />
          {inQuestions
            ? <span className="flex items-center gap-1"><ChevronRight size={15} color="var(--color-faint)" /><Crumb label="Questions" onClick={() => navigate([QV])} active /></span>
            : path.map((seg, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight size={15} color="var(--color-faint)" />
                <Crumb label={seg} onClick={() => navigate(path.slice(0, i + 1))} active={i === path.length - 1} />
              </span>
            ))}
        </div>

        {atRoot && (data.brief
          ? <div className="card px-4 py-3 mt-1 mb-2 text-sm" style={{ color: 'var(--color-ink-2)' }}>{data.brief}</div>
          : folders.length > 0 && <p className="text-sm mt-1 mb-1" style={{ color: 'var(--color-muted)' }}>Open a folder, then tap to add your files.</p>
        )}

        {/* Questions panel */}
        {inQuestions && (
          <QuestionsView questions={questions} onSave={onSaveAnswer} onPickImages={onPickAnswerImages} onOpenFile={setLightbox} onRemoveFile={removeFile} />
        )}

        {!inQuestions && (
          <>
            {/* Questions entry (root only) */}
            {atRoot && questions.length > 0 && (
              <button onClick={() => navigate([QV])} className="card flex items-center gap-3 text-left w-full mt-2" style={{ padding: '14px 16px' }}>
                <MessageSquare size={22} color="var(--color-primary)" style={{ flexShrink: 0 }} />
                <span className="flex-1 min-w-0">
                  <span className="block font-medium" style={{ color: 'var(--color-ink)' }}>Questions</span>
                  <span className="block text-xs" style={{ color: answeredCount === questions.length ? 'var(--color-success)' : 'var(--color-faint)' }}>{answeredCount} of {questions.length} answered</span>
                </span>
                <ChevronRight size={18} color="var(--color-faint)" />
              </button>
            )}

            {/* Folders */}
            {folders.length > 0 && (
              <div className="grid gap-2 mt-2">
                {folders.map((f) => {
                  const r = rollup(f)
                  return (
                    <button key={f.name} onClick={() => navigate([...path, f.name])}
                      className="card flex items-center gap-3 text-left" style={{ padding: '14px 16px' }}>
                      <Folder size={22} color="var(--color-primary)" style={{ flexShrink: 0 }} />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium truncate" style={{ color: 'var(--color-ink)' }}>{f.name}</span>
                        <span className="block text-xs" style={{ color: 'var(--color-faint)' }}>
                          {r.slots} {r.slots === 1 ? 'item' : 'items'}{r.files > 0 ? ` · ${r.files} uploaded` : ''}
                        </span>
                      </span>
                      <ChevronRight size={18} color="var(--color-faint)" />
                    </button>
                  )
                })}
              </div>
            )}

            {/* Slots (upload targets) */}
            {slots.length > 0 && (
              <div className="grid gap-4 mt-3">
                {slots.map((s) => <SlotCard key={s.path} slot={s} onUpload={(files) => upload(s, files)} onOpen={setLightbox} onRemove={removeFile} />)}
              </div>
            )}

            {level.length === 0 && !(atRoot && questions.length > 0) && <p className="text-sm mt-4" style={{ color: 'var(--color-muted)' }}>This folder is empty.</p>}
          </>
        )}

        <p className="text-center text-xs mt-8" style={{ color: 'var(--color-faint)' }}>
          {data.expires_at ? `Link expires ${new Date(data.expires_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · ` : ''}Secured by Reputifly
        </p>
        <div className="safe-bottom" style={{ height: 24 }} />
      </main>

      {lightbox && <Preview file={lightbox} onClose={() => setLightbox(null)} />}
      {/* hide the tray while a preview is open so it never covers the image/video controls */}
      {!lightbox && <UploadTray jobs={jobs} dismiss={dismissJob} retry={retry} retryAll={retryAll} />}
    </div>
  )
}

export function Crumb({ label, onClick, active, root }: { label: string; onClick: () => void; active: boolean; root?: boolean }) {
  return (
    <button onClick={onClick} className="truncate"
      style={{
        maxWidth: 240, padding: '4px 8px', borderRadius: 'var(--radius-sm)',
        fontWeight: active ? 600 : 500, fontSize: root ? 15 : 14,
        color: active ? 'var(--color-ink)' : 'var(--color-primary)',
        background: 'transparent', cursor: active ? 'default' : 'pointer',
      }}>
      {label}
    </button>
  )
}

function SlotCard({ slot, onUpload, onOpen, onRemove }: { slot: SlotNode; onUpload: (files: FileList | File[]) => void; onOpen: (f: UploadedFile) => void; onRemove: (f: UploadedFile) => void }) {
  const [drag, setDrag] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const awaitingRef = useRef(false)   // picker open, file not yet delivered to onChange
  const full = slot.uploaded.length >= slot.count
  const remaining = slot.count - slot.uploaded.length
  // OPEN slot (agent gave no fixed count): no "x/N" target, no "N more" — the client
  // just adds as many as they need. We still show a quiet "N added" once they have files.
  const showCount = slot.fixed !== false

  // iOS prepares/transcodes a large video AFTER the picker closes but BEFORE the change
  // event fires — on screen NOTHING happens, so people tap again and again (the "5 taps").
  // When the page regains focus (picker dismissed) and the file hasn't arrived yet, show a
  // "Preparing…" state and block re-taps until onChange (or a safety timeout) clears it.
  useEffect(() => {
    const onFocus = () => { if (awaitingRef.current) window.setTimeout(() => { if (awaitingRef.current) setPreparing(true) }, 500) }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])
  useEffect(() => { if (!preparing) return; const t = setTimeout(() => { awaitingRef.current = false; setPreparing(false) }, 30000); return () => clearTimeout(t) }, [preparing])
  const openPicker = () => { if (preparing) return; awaitingRef.current = true; inputRef.current?.click() }
  const onPicked = (files: FileList | null) => { awaitingRef.current = false; setPreparing(false); if (files?.length) onUpload(files) }

  return (
    <section className="card" style={{ padding: 18 }}>
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-semibold" style={{ color: 'var(--color-ink)' }}>{slot.name}</h2>
        <span className="badge">{typeLabel(slot.type)}</span>
        {showCount
          ? <span className="text-xs" style={{ color: full ? 'var(--color-success)' : 'var(--color-faint)' }}>{slot.uploaded.length}/{slot.count}</span>
          : slot.uploaded.length > 0 && <span className="text-xs" style={{ color: 'var(--color-faint)' }}>{slot.uploaded.length} added</span>}
      </div>
      {slot.note && <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>{slot.note}</p>}

      {!full && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) onUpload(e.dataTransfer.files) }}
          onClick={openPicker}
          role="button" tabIndex={0} aria-busy={preparing}
          className="mt-3 flex flex-col items-center justify-center text-center"
          style={{
            border: `1.5px dashed ${drag ? 'var(--color-primary)' : 'var(--color-line-2)'}`,
            background: drag ? 'var(--color-primary-soft)' : 'var(--color-surface-2)',
            borderRadius: 16, padding: '22px 16px', minHeight: 100, transition: 'all .15s var(--ease)',
            cursor: preparing ? 'default' : 'pointer', opacity: preparing ? 0.9 : 1,
          }}>
          {preparing ? (
            <>
              <Spinner size={20} />
              <p className="text-sm mt-2" style={{ color: 'var(--color-ink-2)', fontWeight: 600 }}>Preparing your file…</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-faint)' }}>Large videos take a moment — no need to tap again</p>
            </>
          ) : (
            <>
              <Upload size={20} color="var(--color-primary)" />
              <p className="text-sm mt-2" style={{ color: 'var(--color-ink-2)' }}>
                <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Tap to upload</span> or drag here
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-faint)' }}>{showCount ? `Any file · ${remaining} more` : 'Any file · add as many as you need'}</p>
            </>
          )}
          <input ref={inputRef} type="file" multiple hidden
            onChange={(e) => { onPicked(e.target.files); e.target.value = '' }} />
        </div>
      )}

      {slot.uploaded.length > 0 && (
        <div className="grid gap-2 mt-3">
          {slot.uploaded.map((f) => <FileRow key={f.id} file={f} onOpen={onOpen} onRemove={onRemove} />)}
        </div>
      )}
    </section>
  )
}

// One row per item (identical on mobile + desktop): thumbnail left, name + size
// (name ellipsis-truncates but always keeps its extension), remove-X right. Click
// the row body → preview popup.
export function FileRow({ file, onOpen, onRemove }: { file: UploadedFile; onOpen: (f: UploadedFile) => void; onRemove: (f: UploadedFile) => void }) {
  const fileUrl = useFileUrl()
  const [confirming, setConfirming] = useState(false)
  const kind = mimeKind(file.mime || '')
  const { base, ext } = splitName(file.name)
  return (
    <div className="flex items-center rf-pop-in" style={{ border: '1px solid var(--color-line)', background: 'var(--color-surface)', borderRadius: 12, overflow: 'hidden' }}>
      <button onClick={() => onOpen(file)} className="flex items-center gap-3 flex-1 min-w-0 text-left" style={{ padding: 8, background: 'transparent', cursor: 'pointer' }}>
        <span className="flex items-center justify-center" style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: 'var(--color-surface-2)' }}>
          {kind === 'image'
            ? <img src={fileUrl(file.id)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : kind === 'video' ? <Film size={20} color="var(--color-faint)" />
              : kind === 'pdf' ? <FileText size={20} color="var(--color-faint)" />
                : <FileText size={20} color="var(--color-faint)" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--color-ink)' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{base}</span>
            {ext && <span style={{ flexShrink: 0 }}>{ext}</span>}
          </span>
          <span className="block" style={{ fontSize: 11.5, color: 'var(--color-faint)' }}>{formatBytes(file.size)}{file.width && file.height ? ` · ${file.width}×${file.height}` : ''}</span>
        </span>
      </button>
      <div className="flex items-center" style={{ flexShrink: 0, paddingRight: 8, paddingLeft: 4 }}>
        {confirming ? (
          <span className="flex items-center gap-2">
            <button className="text-xs font-semibold" style={{ color: 'var(--color-danger)', padding: '4px 6px' }} onClick={() => { setConfirming(false); onRemove(file) }}>Remove</button>
            <button className="text-xs" style={{ color: 'var(--color-muted)', padding: '4px 6px' }} onClick={() => setConfirming(false)}>Cancel</button>
          </span>
        ) : (
          <button className="btn--icon" aria-label="Remove file" title="Remove" onClick={() => setConfirming(true)} style={{ color: 'var(--color-faint)' }}><X size={18} /></button>
        )}
      </div>
    </div>
  )
}

// Tap a row → this popup. Images show inline, video plays inline; anything else
// (pdf/doc/zip/…) shows an icon + download. Download is top-LEFT, close X top-RIGHT.
// Click-outside / Esc also close.
export function Preview({ file, onClose }: { file: UploadedFile; onClose: () => void }) {
  const fileUrl = useFileUrl()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
  }, [onClose])
  const kind = mimeKind(file.mime || '')
  const url = fileUrl(file.id)
  // Download URL forces Content-Disposition: attachment (server &dl=1). The inline
  // `url` above is for the preview <img>/<video>; this one is for SAVING — on iOS the
  // attachment header makes Safari show its save/share sheet instead of ignoring the
  // `download` attribute and navigating to / rendering the bytes in place (which is
  // why some downloads "opened a random link" or showed the not_found JSON inline).
  const dlUrl = url + (url.includes('?') ? '&' : '?') + 'dl=1'
  const corner = { position: 'fixed' as const, top: 'calc(14px + env(safe-area-inset-top))', width: 42, height: 42, background: 'rgba(255,255,255,0.16)', color: '#fff', backdropFilter: 'blur(6px)' }
  const stop = (e: React.MouseEvent) => e.stopPropagation()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center rf-fade-in" onClick={onClose}
      style={{ background: 'rgba(10,12,16,0.85)', padding: 20 }}>
      <a href={dlUrl} download={file.name} onClick={stop} className="btn--icon flex items-center justify-center" aria-label="Download" title="Download" style={{ ...corner, left: 14 }}><Download size={20} /></a>
      <button className="btn--icon" aria-label="Close" onClick={onClose} style={{ ...corner, right: 14 }}><X size={22} /></button>
      <figure className="rf-pop-in" style={{ margin: 0, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }} onClick={stop}>
        {kind === 'image'
          ? <img src={url} alt={file.name} style={{ maxWidth: '94vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 12, boxShadow: 'var(--shadow-lg)', background: 'var(--color-surface)' }} />
          : kind === 'video'
            ? <video src={url} controls playsInline style={{ maxWidth: '94vw', maxHeight: '78vh', borderRadius: 12, background: '#000', boxShadow: 'var(--shadow-lg)' }} />
            : (
              <div className="card flex flex-col items-center text-center" style={{ padding: '34px 28px', maxWidth: 360 }}>
                <FileText size={44} color="var(--color-primary)" />
                <p className="mt-3 font-medium" style={{ color: 'var(--color-ink)', wordBreak: 'break-word' }}>{file.name}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-faint)' }}>{formatBytes(file.size)}</p>
                <div className="flex items-center gap-3 mt-4">
                  <a href={dlUrl} download={file.name} onClick={stop} className="btn btn--primary inline-flex items-center" style={{ gap: 6, color: 'var(--color-on-primary)' }}><Download size={16} color="var(--color-on-primary)" /> Download</a>
                  {kind === 'pdf' && <a href={url} target="_blank" rel="noopener" onClick={stop} style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: 14 }}>Open</a>}
                </div>
              </div>
            )}
        {kind !== 'file' && kind !== 'pdf' && <figcaption style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, maxWidth: '90vw', textAlign: 'center', wordBreak: 'break-word' }}>{file.name} · {formatBytes(file.size)}</figcaption>}
      </figure>
    </div>
  )
}

// "~2 min left" from the live speed — so a slow-but-working upload reads as working.
function etaText(j: Job): string {
  if (!j.bps || !j.total || j.loaded == null) return ''
  const remain = (j.total - j.loaded) / j.bps
  if (!Number.isFinite(remain) || remain <= 0) return ''
  const m = Math.floor(remain / 60), s = Math.round(remain % 60)
  return m >= 1 ? ` · ~${m} min left` : ` · ~${Math.max(1, s)}s left`
}

function UploadTray({ jobs, dismiss, retry, retryAll }: { jobs: Job[]; dismiss: (id: number) => void; retry: (id: number) => void; retryAll: () => void }) {
  if (!jobs.length) return null
  const active = jobs.filter((j) => j.status === 'uploading').length
  const done = jobs.filter((j) => j.status === 'done').length
  const failed = jobs.filter((j) => j.status === 'error').length
  const retryable = jobs.filter((j) => j.status === 'error' && j.file).length   // network fails (a "folder full" error has no File)
  const heading = active > 0
    ? `Uploading ${done + active > 1 ? `${done + 1} of ${done + active}` : '…'}`
    : failed > 0 ? `${failed} upload${failed > 1 ? 's' : ''} failed` : 'Uploads complete'
  // Always show EVERY error row (pinned first) so a failure can't be sliced out of
  // a larger batch; cap only the non-error rows.
  const errs = jobs.filter((j) => j.status === 'error')
  const rest = jobs.filter((j) => j.status !== 'error').slice(-6)
  const visible = [...errs, ...rest]
  return (
    <div className="fixed z-50 rf-pop-in" style={{ right: 16, left: 16, bottom: 'calc(16px + env(safe-area-inset-bottom))', maxWidth: 360, marginLeft: 'auto' }}>
      <div className="card overflow-hidden" style={{ padding: 0, boxShadow: 'var(--shadow-lg)' }}>
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--color-line)' }}>
          {active > 0 ? <Spinner size={15} /> : failed > 0 ? <AlertCircle size={16} color="var(--color-danger)" /> : <CheckCircle size={16} color="var(--color-success)" />}
          <span className="flex-1 text-sm font-medium" style={{ color: 'var(--color-ink)' }}>{heading}</span>
          {active === 0 && retryable > 1 && (
            <button onClick={retryAll} className="text-xs font-semibold" style={{ color: 'var(--color-primary)', padding: '2px 6px', background: 'transparent', cursor: 'pointer' }}>Retry all</button>
          )}
        </div>
        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
          {visible.map((j) => (
            <div key={j.id} className="px-4 py-2.5" style={{ borderTop: '1px solid var(--color-surface-2)' }}>
              <div className="flex items-center gap-2">
                {j.status === 'done' ? <CheckCircle size={15} color="var(--color-success)" />
                  : j.status === 'error' ? <AlertCircle size={15} color="var(--color-danger)" />
                    : <Film size={15} color="var(--color-faint)" style={{ opacity: 0 }} />}
                <span className="flex-1 truncate text-sm" style={{ color: 'var(--color-ink-2)' }}>{j.name}</span>
                {j.status === 'error' && j.file && (
                  <button className="btn--icon" style={{ width: 22, height: 22 }} onClick={() => retry(j.id)} aria-label="Retry"><RefreshCw size={13} color="var(--color-primary)" /></button>
                )}
                {j.status !== 'uploading' && (
                  <button className="btn--icon" style={{ width: 22, height: 22 }} onClick={() => dismiss(j.id)} aria-label="Dismiss"><X size={13} /></button>
                )}
              </div>
              {j.status === 'uploading' && (j.progress >= 0.999
                // Bytes are all sent; the server is validating + virus-scanning (can take
                // seconds for a big video). Show a live "Finishing up…" pulse so a large
                // upload never looks frozen at a static 100% bar.
                ? <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
                    <div style={{ flex: 1, height: 4, borderRadius: 9999, background: 'var(--color-primary)', animation: 'rf-pulse 1.2s ease-in-out infinite' }} />
                    <span className="text-xs" style={{ color: 'var(--color-faint)', whiteSpace: 'nowrap' }}>Finishing up…</span>
                  </div>
                : <>
                    <div style={{ height: 4, borderRadius: 9999, background: 'var(--color-surface-3)', marginTop: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.round(j.progress * 100)}%`, background: 'var(--color-primary)', borderRadius: 9999, transition: 'width .2s var(--ease)' }} />
                    </div>
                    {/* Legible "23.4 of 110 MB · 1.2 MB/s · ~2 min left" so a slow upload looks alive, not stuck. */}
                    <div className="flex items-center justify-between" style={{ marginTop: 4, gap: 8 }}>
                      <span className="text-xs truncate" style={{ color: j.retrying ? 'var(--color-primary)' : 'var(--color-faint)' }}>
                        {j.retrying ? 'Reconnecting…' : (j.total ? `${formatBytes(j.loaded || 0)} of ${formatBytes(j.total)}` : 'Starting…')}
                      </span>
                      {!j.retrying && j.bps ? <span className="text-xs" style={{ color: 'var(--color-faint)', whiteSpace: 'nowrap' }}>{formatBytes(j.bps)}/s{etaText(j)}</span> : null}
                    </div>
                  </>
              )}
              {j.status === 'error' && <p className="text-xs mt-1" style={{ color: 'var(--color-danger)' }}>{j.error}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
